import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pbLevelIndex } from '@/data/mockSafeData';
import type { ActionItem, TipItem } from '@/api/types';
import type { FeedbackItem } from '../utils/buildFeedbackItems';

export interface KudosCarouselProps {
  strengths:   FeedbackItem[];
  t:           (key: string) => string;
  actionsData: ActionItem[];
  tipsData:    TipItem[];
}

/** Renders an icon from the catalog: FA class name or uploaded image URL. */
function FundIcon({ icon }: { icon?: string }) {
  if (!icon)               return <i className="fas fa-circle" />;
  if (icon.startsWith('fa-')) return <i className={`fas ${icon}`} />;
  return <img src={icon} alt="" className="ud-cs-icon-img" />;
}

export function KudosCarousel({ strengths, t, actionsData, tipsData }: KudosCarouselProps) {
  const { i18n } = useTranslation();
  const lang  = i18n.language.split('-')[0];
  const [slide, setSlide] = useState(0);
  const total = strengths.length;

  const item = strengths[slide];
  if (!item) return null;

  const name  = item.name;
  const icon  = item.icon;
  const color = item.color;

  const lvl = pbLevelIndex(item.score);

  const action = actionsData.find(entry =>
    entry.fundamentalId === item.fundamentalId && entry.active &&
    item.score >= entry.threshold && item.score <= entry.thresholdMax,
  ) ?? actionsData.find(entry => entry.fundamentalId === item.fundamentalId && entry.active);

  const recTip = tipsData.find(tip =>
    tip.fundamentalId === item.fundamentalId && tip.tipType === 'reconocer' && tip.active,
  );
  const quote = recTip
    ? (recTip.translations?.[lang] ?? recTip.tipText)
    : '';

  return (
    <div className="ud-card ud-kudos-card">
      <div className="ud-section-head">
        <div className="ud-icon-box green"><i className="fas fa-heart" /></div>
        <div>
          <h3>{t('safe.kudosTitle')}</h3>
          <small>{t('safe.kudosSub')}</small>
        </div>
        {total > 1 && (
          <div className="ud-cs-nav ud-cs-nav--head">
            <button
              type="button"
              className="ud-cs-btn"
              onClick={() => setSlide((slide - 1 + total) % total)}
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span className="ud-cs-counter">{slide + 1} / {total}</span>
            <button
              type="button"
              className="ud-cs-btn"
              onClick={() => setSlide((slide + 1) % total)}
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>
        )}
      </div>

      <div className="ud-carousel">
        <div className="ud-cs-slide active">
          <div className="ud-cs-top">
            <div className="ud-cs-icon" style={{ color }}>
              <FundIcon icon={icon} />
            </div>
            <div className="ud-cs-name">
              {name}
              <span className={`ud-cs-badge ud-cs-badge--lvl${lvl}`}>
                {t(`safe.level${lvl}`)}
              </span>
            </div>
          </div>

          <div className="ud-cs-peers">
            {item.responses} {t('safe.popup.peersOpined')} · {t('safe.thisMonth')}
          </div>

          <div className="ud-cs-pills">
            {quote && (
              <div className="ud-cs-pill reconocer">
                <div className="ud-cs-pill-label">
                  <i className="fas fa-compass" /> {t('survey.tips.reconocer').toUpperCase()}
                </div>
                <div className="ud-cs-pill-text">"{quote}"</div>
              </div>
            )}
            <div className="ud-cs-pill crecer">
              <div className="ud-cs-pill-label">
                <i className="fas fa-seedling" /> {t('survey.tips.crecer').toUpperCase()}
              </div>
              <div className="ud-cs-pill-text">
                {action
                  ? `${action.translations?.[lang]?.title ?? action.title}: ${action.translations?.[lang]?.description ?? action.description}`
                  : '—'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {total > 1 && (
        <div className="ud-cs-dots">
          {Array.from({ length: total }, (_, index) => (
            <span
              key={`dot-${index}`}
              className={`ud-cs-dot${index === slide ? ' active' : ''}`}
              onClick={() => setSlide(index)}
              role="button"
              tabIndex={0}
              onKeyDown={event => { if (event.key === 'Enter') setSlide(index); }}
              aria-label={`${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
